import { CompanyContactId, SuretyAccountId } from "@inrev/common";
import {
	useAdminDeleteCompanyContact,
	useAdminFetchAccountCompanies,
	useAdminFetchAccountLatestIndemnityAgreement,
} from "@portal/api/account/admin";
import { CompanyContactCard } from "@portal/components/features/contact-cards";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { FC, useState } from "react";
import { AdminCompanyModal } from "../../../../admin/account/shared/AccountCompanyModal";
import { SectionHeaderWithAdd } from "./SectionHeaderWithAdd.tsx";

export type AdminAccountCompaniesSectionProps = {
	accountId: SuretyAccountId;
	primaryCompanyId: CompanyContactId;
};

export const AdminAccountCompaniesSection: FC<AdminAccountCompaniesSectionProps> = ({
	accountId,
	primaryCompanyId,
}) => {
	const { companies, companiesError, companiesIsFetching, refetchCompanies } =
		useAdminFetchAccountCompanies(accountId);
	const {
		latestIndemnityAgreement,
		latestIndemnityAgreementError,
		latestIndemnityAgreementIsFetching,
		refetchLatestIndemnityAgreement,
	} = useAdminFetchAccountLatestIndemnityAgreement(accountId);
	const { deleteCompanyContact } = useAdminDeleteCompanyContact(accountId);
	const [selectedCompanyId, setSelectedCompanyId] = useState<CompanyContactId | "new" | undefined>(
		undefined,
	);

	const indemnitorCompanyIds = new Set(
		latestIndemnityAgreement?.indemnitors.companies.map((indemnitor) => indemnitor.id) ?? [],
	);

	const error = companiesError ?? latestIndemnityAgreementError;

	return (
		<div
			data-testid="admin-account-companies-section"
			className="w-full flex flex-col space-y-[20px]"
		>
			{error ? (
				<ReportedRetryableLoadError
					error={error}
					resource="account companies"
					retrying={companiesIsFetching || latestIndemnityAgreementIsFetching}
					onRetry={() => {
						refetchCompanies();
						refetchLatestIndemnityAgreement();
					}}
				/>
			) : !companies || latestIndemnityAgreement === undefined ? (
				<CenteredSpinner />
			) : (
				<>
					<SectionHeaderWithAdd
						title="Companies"
						testId="add-company"
						onAdd={() => setSelectedCompanyId("new")}
					/>
					{selectedCompanyId && (
						<AdminCompanyModal
							accountId={accountId}
							companies={companies}
							selectedCompanyId={selectedCompanyId === "new" ? undefined : selectedCompanyId}
							onClose={() => setSelectedCompanyId(undefined)}
						/>
					)}
					<div className="w-full flex gap-[15px] flex-wrap">
						{!companies.length && "None"}
						{companies.map((company) => {
							const canDelete =
								company.id !== primaryCompanyId && !indemnitorCompanyIds.has(company.id);
							const { contract } = company.config;
							return (
								<CompanyContactCard
									key={company.id}
									company={company}
									statusCells={[
										{ label: "Indemnifies", active: contract.includeInIndemnity },
										{ label: "Underwriting", active: contract.includeInUnderwriting },
									]}
									onEdit={() => setSelectedCompanyId(company.id)}
									onDelete={canDelete ? () => deleteCompanyContact(company.id) : undefined}
								/>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
};
